import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Box, Button, Flex, HStack, Icon, IconButton, Select, Text } from '@chakra-ui/react';
import { Clock, CircleCheck, ThumbsUp, Flag, Ban, ShoppingCart, Package, Truck, CircleX, Mail, MailOpen, CircleHelp, Columns3, ChevronLeft, ChevronRight } from 'lucide-react';
import { NUMEROS, ROTULO_SECAO } from '../VendaCardV2/ui';

// Layout kanban da Mensageria, no molde do kanban de Vendas. Não busca nada
// sozinho: recebe as colunas já filtradas e ordenadas pelo backend (mesmos
// filtros e mesma ordem da lista) e desenha cada conversa com o card da lista.

const CORES = {
    gray: { fundo: 'gray.100', cor: 'gray.600' },
    orange: { fundo: 'orange.100', cor: 'orange.600' },
    blue: { fundo: 'blue.100', cor: 'blue.600' },
    green: { fundo: 'green.100', cor: 'green.600' },
    red: { fundo: 'red.100', cor: 'red.600' },
    yellow: { fundo: 'yellow.100', cor: 'yellow.700' },
    purple: { fundo: 'purple.100', cor: 'purple.600' },
    teal: { fundo: 'teal.100', cor: 'teal.600' },
};

const coluna = (chave, texto, scheme, icone) => ({ chave, texto, scheme, icone });

const OUTROS = coluna('outros', 'Outros', 'gray', CircleHelp);

// Chaves iguais às de MensageriaController::colunaKanban. Situação segue os
// selos do card: Sem responder > Sem resposta possível > Finalizada > Só agradecimento > Respondida.
export const AGRUPAMENTOS_MENSAGERIA = {
    situacao: {
        rotulo: 'Situação da conversa',
        colunas: [
            coluna('sem_responder', 'Sem responder', 'orange', Clock),
            coluna('respondida', 'Respondida', 'green', CircleCheck),
            coluna('agradecimento', 'Só agradecimento', 'teal', ThumbsUp),
            coluna('finalizada', 'Finalizada', 'purple', Flag),
            coluna('bloqueada', 'Sem resposta possível', 'gray', Ban),
        ],
    },
    status: {
        rotulo: 'Status da venda',
        colunas: [
            coluna('confirmada', 'Confirmada', 'gray', ShoppingCart),
            coluna('enviar', 'Para enviar', 'yellow', Package),
            coluna('transito', 'Em trânsito', 'blue', Truck),
            coluna('concluida', 'Concluída', 'green', CircleCheck),
            coluna('cancelada', 'Cancelada', 'red', CircleX),
        ],
    },
    leitura: {
        rotulo: 'Leitura',
        colunas: [
            coluna('nao_lida', 'Não lidas', 'blue', Mail),
            coluna('lida', 'Lidas', 'gray', MailOpen),
        ],
    },
};

const VAZIA = { total: 0, data: [] };

const MensageriaKanban = ({ colunas, agrupar, agruparDados, onAgrupar, total, isMobile, porColuna, onCarregarMais, renderConversa }) => {
    const trilhoRef = useRef(null);
    const [rolagem, setRolagem] = useState({ esquerda: false, direita: false });
    const [carregandoColuna, setCarregandoColuna] = useState(null);
    // As colunas seguem o agrupamento dos dados recebidos; o seletor pode já
    // estar no próximo enquanto a resposta não chega (evita piscar vazio).
    const grupoDados = agruparDados || agrupar;

    const medirRolagem = useCallback(() => {
        const el = trilhoRef.current;
        if (!el) return;
        const esquerda = el.scrollLeft > 2;
        const direita = el.scrollLeft + el.clientWidth < el.scrollWidth - 2;
        setRolagem((atual) => (atual.esquerda === esquerda && atual.direita === direita ? atual : { esquerda, direita }));
    }, []);

    useEffect(() => {
        const el = trilhoRef.current;
        if (!el) return undefined;
        medirRolagem();
        el.addEventListener('scroll', medirRolagem, { passive: true });
        const observador = new ResizeObserver(medirRolagem);
        observador.observe(el);
        return () => {
            el.removeEventListener('scroll', medirRolagem);
            observador.disconnect();
        };
    }, [medirRolagem]);

    // Volta ao início só quando muda o agrupamento: a atualização automática
    // (a cada 1 min) não pode jogar a rolagem para o começo.
    useEffect(() => {
        if (trilhoRef.current) trilhoRef.current.scrollLeft = 0;
    }, [grupoDados]);

    useEffect(() => {
        medirRolagem();
        setCarregandoColuna(null);
    }, [colunas, medirRolagem]);

    const rolar = (sentido) => {
        const el = trilhoRef.current;
        if (!el) return;
        const col = el.querySelector('[data-coluna]');
        const passo = col ? col.getBoundingClientRect().width + 12 : el.clientWidth * 0.8;
        el.scrollBy({ left: sentido * passo, behavior: 'smooth' });
    };

    const definicao = AGRUPAMENTOS_MENSAGERIA[grupoDados] || AGRUPAMENTOS_MENSAGERIA.situacao;
    const lista = colunas?.outros ? [...definicao.colunas, OUTROS] : definicao.colunas;

    return (
        <>
            <Flex align="center" justify="space-between" gap={3} wrap="wrap" textAlign="left">
                <HStack spacing={2}>
                    <IconButton aria-label="Colunas anteriores" icon={<Icon as={ChevronLeft} boxSize="18px" />} size="sm" variant="outline" bg="white" borderColor="gray.200"
                        color="gray.700" borderRadius="md" isDisabled={!rolagem.esquerda} onClick={() => rolar(-1)} />
                    <Icon as={Columns3} boxSize="14px" color="gray.500" />
                    <Text {...ROTULO_SECAO} color="gray.500" as="label" htmlFor="mensageria-kanban-colunas">Colunas por</Text>
                    <Select id="mensageria-kanban-colunas" size="sm" w="auto" minW="180px" bg="white" borderColor="gray.200" borderRadius="md" fontSize="13px" fontWeight="600"
                        value={agrupar} onChange={(e) => onAgrupar(e.target.value)}>
                        {Object.entries(AGRUPAMENTOS_MENSAGERIA).map(([valor, a]) => <option key={valor} value={valor}>{a.rotulo}</option>)}
                    </Select>
                </HStack>
                <HStack spacing={2}>
                    <Text fontSize="12px" color="gray.500" sx={NUMEROS}>{total} conversa{total !== 1 ? 's' : ''}</Text>
                    <IconButton aria-label="Próximas colunas" icon={<Icon as={ChevronRight} boxSize="18px" />} size="sm" variant="outline" bg="white" borderColor="gray.200"
                        color="gray.700" borderRadius="md" isDisabled={!rolagem.direita} onClick={() => rolar(1)} />
                </HStack>
            </Flex>
            <Flex ref={trilhoRef} gap={3} align="flex-start" overflowX="auto" pb={3} pt={2} textAlign="left"
                sx={{ scrollSnapType: isMobile ? 'x mandatory' : undefined, WebkitOverflowScrolling: 'touch' }}>
                {lista.map((col) => {
                    const cor = CORES[col.scheme] || CORES.gray;
                    const dados = colunas?.[col.chave] || VAZIA;
                    const faltam = dados.total - dados.data.length;
                    return (
                        <Box key={col.chave} data-coluna={col.chave} flex={isMobile ? '0 0 88%' : (dados.total ? '1 0 330px' : '0 0 190px')} minW={isMobile ? undefined : (dados.total ? '330px' : '190px')}
                            bg="#E4E8EE" borderRadius="xl" p={2} sx={{ scrollSnapAlign: 'start' }}>
                            <Flex align="center" justify="space-between" px={1.5} pt={1} pb={2.5}>
                                <HStack spacing={2} minW={0}>
                                    <Flex w="22px" h="22px" flex="none" borderRadius="md" bg={cor.fundo} color={cor.cor} align="center" justify="center">
                                        <Icon as={col.icone} boxSize="13px" strokeWidth={2.25} />
                                    </Flex>
                                    <Text {...ROTULO_SECAO} color="gray.700">{col.texto}</Text>
                                </HStack>
                                <Box px={2} borderRadius="full" bg="white" color="gray.700" fontSize="11px" fontWeight="800" lineHeight="20px" sx={NUMEROS}>{dados.total}</Box>
                            </Flex>
                            <Flex direction="column" gap={2}>
                                {dados.total === 0 && (
                                    <Text fontSize="12px" color="gray.500" textAlign="center" py={6}>Nenhuma conversa</Text>
                                )}
                                {dados.data.map((conversa) => (
                                    <React.Fragment key={conversa.pack_id}>{renderConversa(conversa)}</React.Fragment>
                                ))}
                                {faltam > 0 && (
                                    <Button size="sm" variant="ghost" color="gray.600" fontWeight="600" fontSize="12px" _hover={{ bg: 'whiteAlpha.700' }}
                                        isLoading={carregandoColuna === col.chave} loadingText="Carregando"
                                        onClick={() => { setCarregandoColuna(col.chave); onCarregarMais(col.chave, dados.data.length); }}>
                                        Carregar mais {Math.min(porColuna, faltam)} de {faltam}
                                    </Button>
                                )}
                            </Flex>
                        </Box>
                    );
                })}
            </Flex>
        </>
    );
};

export default MensageriaKanban;
